import React, { useState } from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { ChevronDown, ChevronRight } from "@gaming/xds-react-js-icons";

// Leaf imports, not the `clarity` barrel — see the note in useLandingCta.ts.
import { trackClarityEvent } from "../../../../clarity/clarityClient";
import { ClarityEvents } from "../../../../clarity/clarityEvents";
import { FormattedText } from "../../../../components/FormattedText";
import { useStrings } from "../../../../localization/localizationHelpers";

import * as styles from "./PartnerFaqSection.module.css";

export const PartnerFaqSection = () => {
  const [title, q1, a1, q2, a2, q3, a3, q4, a4, q5, a5] = useStrings(
    "landingFaqTitle",
    "landingFaqQuestion1",
    "landingFaqAnswer1",
    "landingFaqQuestion2",
    "landingFaqAnswer2",
    "landingFaqQuestion3",
    "landingFaqAnswer3",
    "landingFaqQuestion4",
    "landingFaqAnswer4",
    "landingFaqQuestion5",
    "landingFaqAnswer5"
  );

  const items = [
    { question: q1, answer: a1 },
    { question: q2, answer: a2 },
    { question: q3, answer: a3 },
    { question: q4, answer: a4 },
    { question: q5, answer: a5 },
  ];

  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggle = (index: number) => {
    const isOpening = openIndex !== index;

    // Only expansions are tracked. Firing on collapse too would double count
    // every question a visitor opens and then closes. This deliberately sits
    // outside the state updater, which React may invoke more than once.
    if (isOpening) {
      trackClarityEvent(ClarityEvents.landingFaqExpanded);
    }

    setOpenIndex(isOpening ? index : null);
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>{title}</h2>
      <div className={styles.list}>
        {items.map((item, index) => {
          const isOpen = openIndex === index;
          const buttonId = `landing-faq-button-${index}`;
          const panelId = `landing-faq-panel-${index}`;
          return (
            <div key={item.question} className={styles.item}>
              <button
                className={styles.question}
                onClick={() => toggle(index)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                id={buttonId}
              >
                <Icon
                  aria-hidden="true"
                  icon={isOpen ? ChevronDown : ChevronRight}
                  iconSize={IconSize.Small}
                />
                <span className={styles.questionText}>{item.question}</span>
              </button>
              {isOpen && (
                <div
                  id={panelId}
                  className={styles.answer}
                  role="region"
                  aria-labelledby={buttonId}
                >
                  <FormattedText text={item.answer} className={styles.answerText} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};
